import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Assets
import blockIcon from "../../assets/block-white.svg";

// Utils
import { DataContext } from "../../utils/DataContext";
import useLanguage from "../../utils/useLanguage";

// Components
import Loader from "../../components/Loader";
import SectionDivider from "../../components/SectionDivider";
import CustomButton from "../../components/CustomButton";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import TextDiv from "../../components/TextDiv";
import PageWrapper from "../../components/PageWrapper";
import { getSubcompany } from "../../services/subcompanies/subcompaniesServices";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import { blockSubCompany } from "../../services/bank/bankServices";
import AreYouSureModal from "../../components/AreYouSureModal";
import BackToDashboard from "../../components/BackToDashboard";

const ViewSubCompany = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("general");
  const { id } = useParams();
  const { currentDirection } = useLanguage();

  const { error, setError } = useContext(DataContext);

  const [loadingModal, setLoadingModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subCompany, setSubCompany] = useState({
    name: "",
    nameInAr: "",
    city: "",
    country: "",
    address: "",
    nearestLandmark: "",
    website: "",
    email: "",
    phoneNumber: "",
    idNumber: "",
    customsCode: "",
    bankAccount: null,
    comSupAdditionalInfo: null,
    isBlocked: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await getSubcompany(id);

      if (response?.status === 200) {
        setSubCompany(response?.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handleBlockToggle = async () => {
    // TODO: Implement block/unblock logic with backend
    try {
      setLoadingModal(true);
      const response = await blockSubCompany(id);
      if (response?.status === 200) {
        setSubCompany((prev) => ({
          ...prev,
          isBlocked: response?.data?.isBlocked,
        }));
        setShowBlockModal(false);
      }
      setShowBlockModal(false);
    } catch (error) {
      console.error("Error blocking/unblocking subcompany:", error);
      setError("An error occurred");
    } finally {
      setLoadingModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderBankAccount = () => {
    const bankAccount = subCompany?.bankAccount;
    if (!bankAccount) return null;

    return (
      <CollapsibleCard
        title={t("bankAccountInfo")}
        collapsed={true}
        body={
          <>
            <TextDiv
              title="bankName"
              value={bankAccount?.bankName || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="bankAddress"
              value={bankAccount?.bankAddress || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="accountNumber"
              value={bankAccount?.accountNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="accountCurrency"
              value={bankAccount?.accountCurrency || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="SWIFT"
              value={bankAccount?.swiftCode || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="IBAN"
              value={bankAccount?.iban || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            {bankAccount?.branch && (
              <>
                <SectionDivider title="branchDetails" />
                <TextDiv
                  title="branchName"
                  value={bankAccount.branch?.name || "-"}
                  divClasses="item-invoice-div"
                  spanClasses="u-flex_flex-2 t-bold_bold"
                  pClasses="t-bold_normal u-width--min-width_200"
                />

                <TextDiv
                  title="branchAddress"
                  value={bankAccount.branch?.address || "-"}
                  divClasses="item-invoice-div"
                  spanClasses="u-flex_flex-2 t-bold_bold"
                  pClasses="t-bold_normal u-width--min-width_200"
                />
              </>
            )}
          </>
        }
      />
    );
  };

  const renderCompanyInfo = () => {
    const info = subCompany?.comSupAdditionalInfo;
    if (!info) return null;

    return (
      <CollapsibleCard
        title={t("companyAdditionalInformation")}
        collapsed={true}
        body={
          <>
            <SectionDivider title="subCompanyDetails" />

            <TextDiv
              title="addressInAr"
              value={info.addressInAr || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="countryCode"
              value={info.countryCode || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="businessSector"
              value={info.BusinessSector || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="typeOfGoods"
              value={info.typeOfGoods || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="commercialCompanyType"
              value={info.commercialCompanyType || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="certificateIssueDate"
              value={formatDate(info.certificateIssueDate)}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="registrationNumber"
              value={info.registrationNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="originatorType"
              value={info.originatorType || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="generalPurpose"
              value={info.generalPurpose || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="purposeDetails"
              value={info.purposeDetails || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <SectionDivider title="suppliers" />

            <TextDiv
              title="supplier1"
              value={info.supplier1 || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="supplier2"
              value={info.supplier2 || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="supplier3"
              value={info.supplier3 || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <SectionDivider title="financialInformation" />

            <TextDiv
              title="estimatedPaymentsCountPerQuarter"
              value={info.estimatedPaymentsCountPerQuarter || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="estimatedPaymentsValuePerQuarter"
              value={info.estimatedPaymentsValuePerQuarter || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="numberOfEmployees"
              value={info.numberOfEmployees || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="annualRevenue"
              value={info.annualRevenue || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />
          </>
        }
      />
    );
  };

  const renderOwnersAndExecutives = () => {
    const info = subCompany?.comSupAdditionalInfo;
    if (!info) return null;

    return (
      <CollapsibleCard
        title={t("excutiveAndSignatury")}
        collapsed={true}
        body={
          <>
            <SectionDivider title="ceo" />

            <TextDiv
              title="firstNameInEn"
              value={info.sFirstName || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="lastNameInEn"
              value={info.sLastName || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sFirstNameInAr"
              value={info.sFirstNameInAr || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sLastNameInAr"
              value={info.sLastNameInAr || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="dateOfBirth"
              value={formatDate(info.sDateOfBirth)}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="phoneNumber"
              value={info.sPhoneNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="address"
              value={info.sAddress || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="city"
              value={info.sCity || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="country"
              value={info.sCountry || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sPosition"
              value={info.sPosition || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sIsOwner"
              value={info.sIsOwner || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sOwnerPercentage"
              value={info.sOwnerPercentage || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sIsAuthorizedSignatory"
              value={info.sIsAuthorizedSignatory || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sidType"
              value={info.sidType || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <SectionDivider title="authorizedSignatory" />

            <TextDiv
              title="firstNameInEn"
              value={info.aFirstName || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="lastNameInEn"
              value={info.aLastName || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sFirstNameInAr"
              value={info.aFirstNameInAr || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sLastNameInAr"
              value={info.aLastNameInAr || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="dateOfBirth"
              value={formatDate(info.aDateOfBirth)}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="phoneNumber"
              value={info.aPhoneNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="address"
              value={info.aAddress || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="city"
              value={info.aCity || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="country"
              value={info.aCountry || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sIsOwner"
              value={info.aIsOwner || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sOwnerPercentage"
              value={info.aOwnerPercentage || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sIsAuthorizedSignatory"
              value={info.aIsAuthorizedSeniorManaging || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="sidType"
              value={info.aIdType || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="idNumber"
              value={info.aIdNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />
          </>
        }
      />
    );
  };

  return (
    <>
      <PageWrapper className="add-shipped-container-page" withBackButton={true}>

        <h3>{t("subCompanyDetails")}</h3>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className={`add-shipped-container-invoice-part`}>
              <SectionDivider title="basicDetails" />

              <TextDiv
                title="companyNameInEnglish"
                value={subCompany?.name}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="companyNameInArabic"
                value={subCompany?.nameInAr}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="email"
                value={subCompany?.email}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="phoneNumber"
                value={subCompany?.phoneNumber}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="website"
                value={subCompany?.website}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="idNumber"
                value={subCompany?.idNumber}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="customsCode"
                value={subCompany?.customsCode}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <SectionDivider title="addressDetails" />

              <TextDiv
                title="address"
                value={subCompany?.address}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="nearestLandMark"
                value={subCompany?.nearestLandmark}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="city"
                value={subCompany?.city || "-"}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />

              <TextDiv
                title="country"
                value={subCompany?.country || "-"}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width--min-width_200"
              />
            </div>

            <SectionDivider title="additionalInfo" />

            {/* Additional Information Sections */}
            {subCompany?.comSupAdditionalInfo && (
              <div className="u-margin--top_400">
                {renderCompanyInfo()}
                <div className="u-margin--top_400">
                  {renderOwnersAndExecutives()}
                </div>
              </div>
            )}

            {/* Bank Account Section */}
            {subCompany?.bankAccount && (
              <div className="u-margin--top_400">{renderBankAccount()}</div>
            )}
            <div className="o-column o-column--vertical-align_end u-margin--top_400">
              <ErrorMessage>{t(error)}</ErrorMessage>
              <section className={classes.actions}>
                <CustomButton
                  variant={subCompany?.isBlocked ? "green" : "red"}
                  title={t(subCompany?.isBlocked ? "unblock" : "block")}
                  onClick={() => setShowBlockModal(true)}
                  icon={blockIcon}
                  iconSize={20}
                />
              </section>
            </div>
          </>
        )}
      </PageWrapper>

      {/* Block/Unblock Modal */}
      {showBlockModal && (
        <AreYouSureModal
          onClose={() => setShowBlockModal(false)}
          onConfirm={() => {
            handleBlockToggle();
            setShowBlockModal(false);
          }}
          customMessage={
            subCompany?.isBlocked
              ? "areYouSureYouWantToUnblockTheCompany"
              : "areYouSureYouWantToBlockTheCompany"
          }
          closeTitle="cancel"
          confirmTitle="confirm"
          confirmIconType="save"
          confirm={true}
        />
      )}
    </>
  );
};

export default ViewSubCompany;
